import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Users, User, Search, Building2, UserCheck, Briefcase, Eye, CheckCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const ResponsableStagiaires = ({ user }) => {
  const [stagiaires, setStagiaires] = useState([]);
  const [centreNom, setCentreNom] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [stagRes, centreRes] = await Promise.all([
        axios.get('/responsable/stagiaires'),
        axios.get('/responsable/mon-centre')
      ]);
      setStagiaires(stagRes.data || []);
      setCentreNom(centreRes.data?.nom || '');
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const filteredStagiaires = stagiaires.filter(s => {
    const fullName = `${s.user_info?.prenom || ''} ${s.user_info?.nom || ''}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="responsable-stagiaires">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-[#0F172A] mb-2">
            Stagiaires - Centre {centreNom}
          </h1>
          <p className="text-slate-600">{stagiaires.length} stagiaire(s) dans ce centre</p>
        </div>
      </div>

      {/* Recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
        <Input
          placeholder="Rechercher un stagiaire..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Liste */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {filteredStagiaires.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucun stagiaire trouvé</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {filteredStagiaires.map(stagiaire => (
              <div key={stagiaire.user_id} className="p-4 hover:bg-slate-50">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-16 bg-[#8DC63F]/20 rounded-md flex items-center justify-center">
                    <span className="text-[#8DC63F] font-semibold">
                      {stagiaire.user_info?.prenom?.[0]}{stagiaire.user_info?.nom?.[0]}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-[#0F172A]">
                        {stagiaire.user_info?.prenom} {stagiaire.user_info?.nom}
                      </p>
                      {stagiaire.user_info?.numero_candidat && (
                        <span className="text-xs font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full border border-slate-200" data-testid={`numero-candidat-${stagiaire.user_id}`}>
                          N°{stagiaire.user_info.numero_candidat}
                        </span>
                      )}
                      {stagiaire.has_declaration && (
                        <Badge className="bg-blue-600 text-white text-[10px] px-1.5 py-0" data-testid={`badge-declare-${stagiaire.user_id}`}>
                          <CheckCircle className="h-3 w-3 mr-0.5" /> Stagiaire déclaré
                        </Badge>
                      )}
                      {stagiaire.competences_total > 0 && (
                        <Badge className={`text-[10px] px-1.5 py-0 ${stagiaire.competences_acquired === stagiaire.competences_total ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-700'}`}>
                          {stagiaire.competences_acquired}/{stagiaire.competences_total} compétences acquises
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600">
                      {stagiaire.user_info?.email}
                    </p>
                    <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                      <span className="capitalize px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                        {stagiaire.statut}
                      </span>
                      {stagiaire.tuteur_info && (
                        <span className="flex items-center gap-1">
                          <UserCheck className="h-3 w-3" />
                          Tuteur: {stagiaire.tuteur_info.prenom} {stagiaire.tuteur_info.nom}
                        </span>
                      )}
                      {stagiaire.entreprise_info && (
                        <span className="flex items-center gap-1">
                          <Briefcase className="h-3 w-3" />
                          {stagiaire.entreprise_info.prenom}
                        </span>
                      )}
                    </div>
                  </div>
                  <Link to={`/responsable/fiche-stagiaire?id=${stagiaire.user_id}`}>
                    <Button variant="outline" size="sm" className="text-[#8DC63F] border-[#8DC63F] hover:bg-[#8DC63F]/10">
                      <Eye className="h-4 w-4 mr-1" />
                      Voir fiche
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResponsableStagiaires;
